<template>
  <Teleport to="body">
    <div class="gift-backdrop" @click.self="close" @keydown.esc="close">
      <section class="gift-dialog" role="dialog" aria-modal="true" aria-labelledby="gift-title">
        <header><h3 id="gift-title">赠送礼物</h3><button type="button" aria-label="关闭" @click="close" :disabled="sending">×</button></header>
        <p>{{ conversation?.type === 'single' ? '送给' : '当前会话' }} {{ conversation?.title }}</p>
        <div class="gift-balance"><span>EzCoin 余额</span><strong>{{ loading ? '正在查询…' : balance === null ? '暂不可用' : ezcoinBalance(balance) }}</strong></div>
        <p v-if="error" class="gift-status" role="alert">{{ language.error(error) }}</p>
        <label for="gift-amount">礼物金额</label>
        <input id="gift-amount" v-model="amount" :disabled="sending || locked || !!success" inputmode="decimal" autocomplete="off" placeholder="请输入金额">
        <label for="gift-note">留言</label>
        <textarea id="gift-note" v-model="message" maxlength="500" :disabled="sending || locked || !!success" placeholder="给对方留句话"></textarea>
        <p class="gift-status" role="status">{{ success || (eligible ? '对方点击领取后到账，24小时未领取自动退回。' : '礼物仅支持赠送给单聊用户。') }}</p>
        <footer :class="{ 'gift-complete': !!success }"><button type="button" @click="close" :disabled="sending">{{ success ? '完成' : '取消' }}</button><button v-if="!success" type="button" class="gift-submit" :disabled="sending || loading || balance === null || !eligible" @click="send">{{ sending ? '正在赠送…' : locked ? '确认赠送结果' : '赠送礼物' }}</button></footer>
      </section>
    </div>
  </Teleport>
</template>
<script setup>
import { useLanguageStore } from '@/stores/language';
const language = useLanguageStore();
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { ezcoinBalance } from '@/utils/walletMoney';
import { appConfig } from '@/config';
import { useSessionStore } from '@/stores/session';
const props = defineProps({ conversation: Object });
const emit = defineEmits(['close']);
const session = useSessionStore();
const amount = ref(''), message = ref(''), balance = ref(null), loading = ref(true), error = ref(''), sending = ref(false), success = ref(''), locked = ref(false);
const eligible = computed(() => props.conversation?.type === 'single' && !!props.conversation?.target && props.conversation.target !== session.userId);
const journalKey = `jc-gift:${session.userId}:${props.conversation?.target}`;
let intent = null;
try { const stored = localStorage.getItem(journalKey); if (stored) { intent = JSON.parse(stored); if (intent.recipient_id !== props.conversation?.target || typeof intent.amount !== 'string' || !/^[A-Za-z0-9_-]{16,128}$/.test(intent.request_id)) throw new Error(); amount.value = intent.amount; message.value = intent.message || ''; locked.value = true; } } catch { intent = null; }
const controllers = new Set();
async function request(path, payload) {
  const controller = new AbortController(); controllers.add(controller);
  const timer = setTimeout(() => controller.abort(), 30000);
  try {
    const response = await fetch(new URL(`/business-api/${path}`, appConfig.serviceOrigin), { method: 'POST', credentials: 'omit', cache: 'no-store', signal: controller.signal, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: session.token, ...payload }) });
    const data = await response.json();
    if (!response.ok || !data.ok) { const e = new Error(data.message || '钱包服务暂不可用'); e.status = response.status; throw e; }
    return data;
  } finally { clearTimeout(timer); controllers.delete(controller); }
}
function close() { if (sending.value) return; if (success.value) localStorage.removeItem(journalKey); emit('close'); }
async function send() {
  if (sending.value || success.value || !eligible.value) return;
  error.value = '';
  if (!/^\d{1,20}(\.\d{1,8})?$/.test(amount.value) || !/[1-9]/.test(amount.value)) { error.value = '请输入大于0的金额，最多8位小数。'; return; }
  if (!intent) {
    intent = { recipient_id: props.conversation.target, amount: amount.value, message: message.value.trim(), request_id: globalThis.crypto.randomUUID() };
    try { localStorage.setItem(journalKey, JSON.stringify(intent)); } catch { intent = null; error.value = '无法保存赠送请求，请检查浏览器存储后重试。'; return; }
  }
  locked.value = true; sending.value = true;
  try {
    const data = await request('wallet-actions.php', { action: 'gift', ...intent });
    balance.value = data.balance_after; success.value = data.message + (data.status === 'pending' && !data.notification_sent ? '，聊天通知稍后送达。' : '');
  } catch (e) {
    error.value = e.message || '连接中断，请重新打开礼物并确认赠送结果。';
    if (e.status === 422) { localStorage.removeItem(journalKey); intent = null; locked.value = false; }
    else error.value += '；请使用同一请求确认结果，勿重复新建赠送。';
  } finally { sending.value = false; }
}
onMounted(async () => {
  try { const data = await request('wallet.php', { asset: 'ezcoin', page: 1, page_size: 5 }); if (!/^\d+(\.\d+)?$/.test(String(data.balances?.ezcoin ?? ''))) throw new Error(); balance.value = String(data.balances.ezcoin); }
  catch { error.value = '余额查询失败，请稍后重新打开礼物弹窗。'; }
  finally { loading.value = false; }
});
onBeforeUnmount(() => controllers.forEach(controller => controller.abort()));
</script>
<style scoped>
.gift-backdrop { position: fixed; inset: 0; z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 16px; background: rgba(20, 30, 45, .35); }
.gift-dialog { width: min(400px, calc(100vw - 32px)); max-height: calc(100dvh - 32px); overflow-y: auto; box-sizing: border-box; padding: 24px; border-radius: 16px; background: var(--panel, #fff); color: var(--text, #202a33); display: grid; gap: 16px; }
.gift-dialog header, .gift-dialog footer { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.gift-dialog footer.gift-complete { justify-content: flex-end; }
.gift-dialog h3, .gift-dialog p { margin: 0; }
.gift-dialog button { padding: 8px 14px; border: 1px solid var(--line, #d9dee7); border-radius: 8px; background: transparent; color: inherit; cursor: pointer; }
.gift-dialog header button { border: 0; font-size: 24px; }
.gift-balance { padding: 16px; border-radius: 10px; background: var(--panel-soft, #f6f7fa); display: flex; justify-content: space-between; gap: 12px; }
.gift-dialog input, .gift-dialog textarea { width: 100%; padding: 12px; box-sizing: border-box; border: 1px solid var(--line, #d9dee7); border-radius: 8px; background: transparent; color: inherit; font-size: 22px; }
.gift-dialog textarea { min-height: 80px; resize: vertical; font-size: 16px; font-family: inherit; }
.gift-status { font-size: 14px; line-height: 1.6; color: var(--muted, #747c89); }
.gift-submit:disabled { opacity: .5; cursor: not-allowed; }
</style>
